/**
 * ScenarioList.jsx
 * Renders a paginated, sortable list/table of Scenarios.
 * Used in the Scenario module main page.
 * - Data source: Redux state (scenarios.list)
 * - Supports sorting, dense mode, pagination, status toggling, and row selection
 * - Intended to match RAVEN design patterns for table/list modules
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Parser } from "html-to-react";
import PropTypes from "prop-types";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useListTableState } from "src/hooks/useListTableState";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	createListCollection,
	IconButton,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Pagination,
	Portal,
	Switch,
	Table,
	MetreaTooltip,
} from "@metrea-ui/v2";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import styles from "./ScenarioList.module.css";
import { EnhancedTableHead, EnhancedTableToolbar } from "src/components/UI/common/EnhancedTableParts";
import CardList from "src/components/UI/CardList/CardList";
import { ChevronLeftRegular, ChevronRightRegular } from "@fluentui/react-icons";

// Utilities
import { scenarioAccess } from "src/utils/scenarioUtils";
import { extendBaseline } from "src/utils/baselineUtils";
import { Session, isDevTeam, isDeveloper } from "src/utils/sessionUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";
import { getShortDateString, getShortDateTimeString, highlight } from "src/utils/formatUtils";
import { isDevSiteNow } from "src/utils/debugUtils";

const SEARCH_FIELDS = ["name", "baseline_name"];

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const statusCollection = createListCollection({
	items: [
		{ label: "Inactive", value: "0", rawValue: 0 },
		{ label: "Active", value: "1", rawValue: 1 },
	],
});

const ScenarioList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const pageMode = props.mode;
	const uiSize = props.size;
	const htmlParser = new Parser();

	const { allUserGroups } = useLookupsData();

	// --- Redux Selectors ---
	const scenarios = useSelector((state) => state.scenarios?.list ?? []);

	// add additonal info to the baseline
	const scenariosWithExtended = useMemo(
		() =>
			(scenarios ?? []).map((s) => ({
				...s,
				baseline: extendBaseline(s.baseline),
				baseline_name: s?.baseline?.name ?? "",
			})),
		[scenarios],
	);

	// Filter by user's access
	const filterScenarios = useMemo(
		() => scenariosWithExtended.filter((x) => scenarioAccess(x, allUserGroups, Session().currUser) >= 1),
		[scenariosWithExtended, allUserGroups],
	);

	// --- Local State ---
	const [showInactive, setShowInactive] = useState(false);
	const [itemSelected, setItemSelected] = useState([]);
	const [searchKeyword, setSearchKeyword] = useState("");

	useEffect(() => {
		debugLog(2, "[ScenarioList][FLOW] mounted", {});
	}, []);

	// Helper to return filtered scenarios based on Active/Inactive toggle.
	const visibleScenarios = useMemo(() => {
		const activeFiltered = showInactive
			? filterScenarios
			: filterScenarios.filter((x) => x.status_id === 1);
		if (!searchKeyword) return activeFiltered;

		const kw = searchKeyword.toLowerCase();
		const result = activeFiltered.filter((u) =>
			SEARCH_FIELDS.some((field) => {
				const val = u[field];
				return typeof val === "string" && val.toLowerCase().includes(kw);
			}),
		);
		debugLog(4, "[ScenarioList][PROCESS] visibleScenarios filter fields", {
			searchFields: SEARCH_FIELDS,
		});

		return result;
	}, [showInactive, filterScenarios, searchKeyword]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		debugLog(3, "[ScenarioList][ACTION] handleSelectionChange", { newSelected: newSelected });
		setItemSelected(newSelected);
	};

	const headCells = [
		{
			id: "name",
			numeric: false,
			disablePadding: false,
			label: "Name",
		},
		{
			id: "baseline_name",
			numeric: false,
			disablePadding: false,
			label: "Baseline Name",
			disableSort: true,
		},
		{
			id: "periodLabel",
			numeric: false,
			disablePadding: false,
			label: "Period",
			disableSort: true,
		},
		{
			id: "currency_symbol",
			numeric: false,
			disablePadding: false,
			label: "Currency Symbol",
		},
		{
			id: "createdAt",
			numeric: false,
			disablePadding: false,
			label: "Date Created",
		},
		{
			id: "updatedAt",
			numeric: false,
			disablePadding: false,
			label: "Last Updated",
		},
		{
			id: "status_id",
			numeric: false,
			disablePadding: false,
			label: "Status",
			align: "center",
		},
	];
	if (showTesting() && isDevTeam()) {
		headCells.unshift({
			id: "uuid",
			numeric: false,
			disablePadding: false,
			label: "UUID",
		});
	}

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		selected,
		visibleRows,
		emptyRows,
		handleRequestSort,
		handleChangePageM1,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		items: visibleScenarios, // Or from Redux selector
		initialOrderBy: "updatedAtTS",
		initialOrder: "desc",
		initialRowsPerPage: 10,
		initialPage: 1,
		selectionMode: true,
		selected: itemSelected,
		onSelectedChange: handleSelectionChange,
	});

	// --- Handlers ---
	const rowClickHandler = (event, item) => {
		debugLog(3, "[ScenarioList][ACTION] rowClickHandler", { item: item });
		// In-app Open should resolve by numeric id; UUID is reserved for explicit deep-link flows.
		const scenarioId = typeof item === "object" && item !== null ? item.id : item;
		props.onSelectScenario(scenarioId);
	};

	const changeStatusHandler = (o, value) => {
		debugLog(3, "[ScenarioList][ACTION] changeStatusHandler", { row: o, value: value });
		debugLog(2, "[ScenarioList][FLOW] onChangeStatus started", { id: o.id, value: value });
		props.onChangeStatus(o.id, value);
	};

	// Handlers for RecordNav (sorting, show inactive, etc.)
	const handleSearchChange = (keyword) => {
		setSearchKeyword(keyword);
		debugLog(7, "[ScenarioList][ACTION] handleSearchChange", { keyword: keyword });
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[ScenarioList][ACTION] toggleInactiveHandler", { option: option });
		setShowInactive(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[ScenarioList][ACTION] sortByHandler", { option: option });
		const mapOrderBy = (ob) =>
			ob === "updatedAt" ? "updatedAtTS" : ob === "createdAt" ? "createdAtTS" : ob;
		setOrderBy(mapOrderBy(option.orderBy));
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[ScenarioList][ACTION] changeRowsPerPageHandler", { option: option });
		const next = Number(option?.id);
		if (!Number.isFinite(next) || next <= 0) return;
		setRowsPerPage(next);
		setPage(0);
		setPageM1(1);
	};

	const renderStatusSelect = (row) => (
		<MetreaSelectRoot
			variant="outline"
			collection={statusCollection}
			size={sizeToSelectSize(uiSize)}
			width="105px"
			name="status_id"
			value={[String(row.status_id ?? "")]}
			onValueChange={(details) => {
				const nextValue = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
				// Keep the existing status-change contract: the direct Select gives us
				// strings, but downstream handlers still expect numeric 0/1 ids here.
				const normalizedValue = Number.isNaN(Number(nextValue)) ? nextValue : Number(nextValue);
				changeStatusHandler(row, normalizedValue);
			}}
		>
			<MetreaSelectTrigger id={`scenario-status-${row.id}`}>
				<MetreaSelectValueText />
			</MetreaSelectTrigger>
			<Portal>
				<MetreaSelectContent>
					{statusCollection.items.map((item) => (
						<MetreaSelectItem key={item.value} item={item}>
							{item.label}
						</MetreaSelectItem>
					))}
				</MetreaSelectContent>
			</Portal>
		</MetreaSelectRoot>
	);

	// Inside your component (replace the current <Paper>...<Table.Root>...</Paper> block):
	const columns = [
		...(showTesting() && isDevTeam()
			? [
					{
						key: "uuid",
						title: "UUID",
						w: 240,
						render: (row) => row.uuid,
					},
				]
			: []),
		{
			key: "name",
			title: "Name",
			w: 480,
			stackGrow: 2,

			// -------------------------------------------------------
			// Show only the plain Name in the visible cell (no description).
			// -------------------------------------------------------
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.name, false)),

			// -------------------------------------------------------
			// Decide per row when to display a tooltip:
			// - If there is a description → show custom Name+Note tooltip even when not truncated
			// - If description is empty → fall back to normal "auto" (only show when truncated)
			// -------------------------------------------------------
			tooltip: (row) => {
				const hasDescription = Boolean(row.description?.trim().length > 0);
				return hasDescription ? "always" : "auto";
			},

			// -------------------------------------------------------
			// Provide the text shown inside the tooltip:
			// - If there's a description → show:
			//       "<Name>\n\nNote:\n<Description>"
			// - Otherwise → return undefined so CardList skips rendering tooltip entirely.
			// -------------------------------------------------------
			tooltipText: (row) => {
				const descriptionExists = Boolean(row.description?.trim().length > 0);
				if (!descriptionExists) return undefined;

				const tooltipLines = [
					row.name,
					"", // blank line for spacing
					"Note:",
					row.description,
				];
				// Join with line breaks so EllipsisTooltip will display multiline correctly
				return tooltipLines.join("\n");
			},
		},
		{
			key: "baseline_name",
			title: "Baseline Name",
			w: 200,
			stackGrow: 2,

			// -------------------------------------------------------
			// Show only the plain Name in the visible cell (no description).
			// -------------------------------------------------------
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.baseline_name, false)),

			// -------------------------------------------------------
			// Decide per row when to display a tooltip:
			// - If there is a description → show custom Name+Note tooltip even when not truncated
			// - If description is empty → fall back to normal "auto" (only show when truncated)
			// -------------------------------------------------------
			tooltip: (row) => {
				const hasDescription = Boolean(
					row.baseline?.description && row.baseline?.description.trim().length > 0,
				);
				return hasDescription ? "always" : "auto";
			},

			// -------------------------------------------------------
			// Provide the text shown inside the tooltip:
			// - If there's a description → show:
			//       "<Name>\n\nNote:\n<Description>"
			// - Otherwise → return undefined so CardList skips rendering tooltip entirely.
			// -------------------------------------------------------
			tooltipText: (row) => {
				const descriptionExists = Boolean(
					row.baseline?.description && row.baseline?.description.trim().length > 0,
				);
				if (!descriptionExists) return undefined;

				const tooltipLines = [
					row.baseline?.name ?? "",
					"", // blank line for spacing
					"Note:",
					row.baseline?.description ?? "",
				];
				// Join with line breaks so EllipsisTooltip will display multiline correctly
				return tooltipLines.join("\n");
			},
		},

		{
			key: "periodLabel",
			title: "Period",
			minW: 90,
			tooltip: "auto",
			render: (row) => row.baseline?.periodLabel,
		},
		{
			key: "currency",
			title: "Currency Symbol",
			minW: 80,
			tooltip: "auto",
			render: (row) =>
				`${row.baseline?.currency_code ?? ""} - ${row.baseline?.currency_symbol ?? ""} ${row.baseline?.currency_unit ?? ""}`,
		},
		{
			key: "createdAt",
			title: "Date Created",
			minW: 80,
			tooltip: "auto",
			render: (row) => getShortDateString(row.createdAt),
		},
		{
			key: "updatedAt",
			title: "Last Updated",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateTimeString(row.updatedAt),
		},
		{
			key: "status_id",
			title: "Status",
			minW: 60,
			tooltip: undefined, // or omit the field to disable the ellipsis tooltip path
			render: (row) => (row?.status_id === 1 ? "Active" : "Inactive"),
		},
	];

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[ScenarioList][FLOW] render", {
		renderCount: renderCount.current,
		scenarios: scenarios,
	});
	debugLog(9, "[ScenarioList][FLOW] renderState", {
		props: props,
		visibleRows: visibleRows,
		visibleScenarios: visibleScenarios,
		rowsPerPage: rowsPerPage,
		pageM1: pageM1,
		selected: selected,
		showInactive: showInactive,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<>
			<Box w="100%">
				{/* Replace Table/Paper block with CardList */}
				<CardList
					rows={visibleRows}
					keyField="id"
					columns={columns}
					toolbarSlot={
						<RecordNav
							data={visibleScenarios}
							showSearch={isDeveloper()}
							searchFields={SEARCH_FIELDS}
							columns={columns}
							onSearchChange={handleSearchChange}
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={(opt) => sortByHandler({ orderBy: opt.orderBy, order: opt.order })}
							onChangeRowsPerPage={(opt) => changeRowsPerPageHandler(opt)}
							callFrom="Listing"
							orderBy={orderBy}
							order={order}
						/>
					}
					showLabels
					density="compact"
					responsive={{
						mode: "auto",
						maxRows: 5,
						minColWidth: 100,
						gap: 12,
					}}
					showVerticalDividers
					paginationSlot={
						<Pagination.Root
							count={visibleScenarios.length}
							pageSize={Number(rowsPerPage) || 10}
							page={pageM1}
							onPageChange={({ page }) => handleChangePageM1(undefined, page)}
						>
							<ButtonGroup variant="ghost" size="2xs">
								<Pagination.PrevTrigger asChild>
									<IconButton>
										<ChevronLeftRegular />
									</IconButton>
								</Pagination.PrevTrigger>
								<Pagination.Items
									render={(page) => (
										<IconButton variant={{ base: "ghost", _selected: "solid" }}>
											{page.value}
										</IconButton>
									)}
								/>
								<Pagination.NextTrigger asChild>
									<IconButton>
										<ChevronRightRegular />
									</IconButton>
								</Pagination.NextTrigger>
							</ButtonGroup>
						</Pagination.Root>
					}
					rowActions={(row) => {
						const actions = [];
						actions.push({
							id: "open",
							label: "Open",
							onSelect: (row) => rowClickHandler(null, row),
						});

						const isActive = String(row.status_id) === "1";
						const canEdit =
							pageMode === "edit" &&
							scenarioAccess(row, allUserGroups, Session().currUser) === 2;
						if (canEdit) {
							actions.push({
								id: "toggle-active",
								label: isActive ? "Make Inactive" : "Make Active",
								onSelect: () => changeStatusHandler(row, isActive ? 0 : 1),
							});
						}

						return actions;
					}}
				/>
			</Box>

			{/* Keep the legacy table mirror behind testing mode while draining its
			    wrapper-backed controls onto direct v2 primitives. */}
			{showTesting() && isDeveloper() && (
				<>
					<Box marginTop="1200px"></Box>
					<Box w="100%">
						<RecordNav
							data={visibleScenarios}
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={sortByHandler}
							onChangeRowsPerPage={changeRowsPerPageHandler}
							orderBy={orderBy}
							order={order}
						/>

						<Box w="100%" mb={3} boxShadow="sm" bg="white" rounded="md">
							{showTesting() && isDevTeam() && (
								<EnhancedTableToolbar numSelected={selected.length} />
							)}
							<Box overflowX="auto">
								<Table.Root
									size={dense ? "sm" : "md"}
									variant="outline"
									aria-labelledby="tableTitle"
								>
									<EnhancedTableHead
										numSelected={selected.length}
										order={order}
										orderBy={orderBy}
										onSelectAllClick={handleSelectAllClick}
										onRequestSort={handleRequestSort}
										rowCount={visibleScenarios.length}
										headCells={headCells}
										uiSize={uiSize}
										styles={styles}
									/>
									<Table.Body>
										{visibleRows.map((row, index) => {
											const isItemSelected = isSelected(row.id);

											return (
												<Table.Row
													key={row.id}
													data-selected={isItemSelected}
													style={{ cursor: "pointer" }}
													className={styles.tableBodyRow}
												>
													{showTesting() && isDevTeam() && (
														<Table.Cell>
															<Checkbox.Root
																checked={isItemSelected}
																onCheckedChange={(next) =>
																	checkHandler(
																		{
																			target: {
																				checked:
																					next === true ||
																					next?.checked === true ||
																					next?.detail?.checked ===
																						true,
																			},
																		},
																		row.id,
																	)
																}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																}}
															>
																<Checkbox.HiddenInput />
																<Checkbox.Control />
															</Checkbox.Root>
														</Table.Cell>
													)}
													{showTesting() && isDevTeam() && (
														<Table.Cell
															onClick={(event) => rowClickHandler(event, row)}
														>
															{row.uuid}
														</Table.Cell>
													)}
													<Table.Cell
														style={{ width: "20%" }}
														onClick={(event) => rowClickHandler(event, row)}
													>
													{row.description?.trim?.() ? (
														<MetreaTooltip content={row.description}>
															<div>{row.name}</div>
														</MetreaTooltip>
													) : (
														<div>{row.name}</div>
													)}
													</Table.Cell>
													<Table.Cell
														style={{ width: "20%" }}
														onClick={(event) => rowClickHandler(event, row)}
													>
													{row.baseline?.description?.trim?.() ? (
														<MetreaTooltip content={row.baseline.description}>
															<div>{row.baseline?.name ?? ""}</div>
														</MetreaTooltip>
													) : (
														<div>{row.baseline?.name ?? ""}</div>
													)}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														<span>{row.baseline?.periodLabel ?? ""}</span>
													</Table.Cell>
													<Table.Cell
														style={{
															textAlign: "center",
															width: "10%",
															fontSize: "0.7rem",
														}}
														onClick={(event) => rowClickHandler(event, row)}
													>
														<span>
															{`${row.baseline?.currency_code ?? ""} - ${row.baseline?.currency_symbol ?? ""} ${row.baseline?.currency_unit ?? ""}`}
														</span>
													</Table.Cell>
													<Table.Cell
														style={{ width: "10%", fontSize: "0.7rem" }}
														onClick={(event) => rowClickHandler(event, row)}
													>
														<span>{getShortDateString(row.createdAt)}</span>
													</Table.Cell>
													<Table.Cell
														style={{ width: "10%", fontSize: "0.7rem" }}
														onClick={(event) => rowClickHandler(event, row)}
													>
														<span>{getShortDateTimeString(row.updatedAt)}</span>
													</Table.Cell>
													<Table.Cell style={{ textAlign: "center" }}>
														{pageMode !== "edit" ||
														scenarioAccess(
															row,
															allUserGroups,
															Session().currUser,
														) !== 2
															? row.status_id === 1
																? "Active"
																: "Inactive"
															: ""}
														{pageMode === "edit" &&
															scenarioAccess(
																row,
																allUserGroups,
																Session().currUser,
															) === 2 &&
															renderStatusSelect(row)}
													</Table.Cell>
												</Table.Row>
											);
										})}
										{emptyRows > 0 && (
											<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
												<Table.Cell
													colSpan={
														(showTesting() && isDevTeam() ? 1 : 0) +
														headCells.length
													}
													style={{ borderBottom: "none", padding: 0 }}
												/>
											</Table.Row>
										)}
									</Table.Body>
								</Table.Root>
							</Box>
						</Box>

						<Box className="flex-auto justify-items-center p-1">
							<Pagination.Root
								count={visibleScenarios.length}
								pageSize={Number(rowsPerPage) || 10}
								page={pageM1}
								onPageChange={({ page }) => handleChangePageM1(undefined, page)}
							>
								<ButtonGroup variant="ghost" size="2xs">
									<Pagination.PrevTrigger asChild>
										<IconButton>
											<ChevronLeftRegular />
										</IconButton>
									</Pagination.PrevTrigger>
									<Pagination.Items
										render={(page) => (
											<IconButton variant={{ base: "ghost", _selected: "solid" }}>
												{page.value}
											</IconButton>
										)}
									/>
									<Pagination.NextTrigger asChild>
										<IconButton>
											<ChevronRightRegular />
										</IconButton>
									</Pagination.NextTrigger>
								</ButtonGroup>
							</Pagination.Root>
						</Box>

						{showTesting() && isDevTeam() && isDevSiteNow() && (
							<Switch.Root
								size="sm"
								className="m-[10px]"
								checked={dense}
								onCheckedChange={(next) =>
									handleChangeDense({
										target: {
											checked:
												next === true ||
												next?.checked === true ||
												next?.detail?.checked === true,
										},
									})
								}
							>
								<Switch.HiddenInput />
								<Switch.Control>
									<Switch.Thumb />
								</Switch.Control>
								<Switch.Label>Dense padding</Switch.Label>
							</Switch.Root>
						)}
					</Box>
				</>
			)}
		</>
	);
};

ScenarioList.propTypes = {
	onSelectScenario: PropTypes.func.isRequired,
	onChangeStatus: PropTypes.func.isRequired,
	mode: PropTypes.string,
	size: PropTypes.string,
};

export default ScenarioList;
